import { useState, useRef, useEffect } from 'react';
import { smartMatch } from './micData';

export function useMicSystem({
  questionsRef,
  currentIdxRef,
  currentIndex,
  livesRef,
  mountedRef,
  onCorrect,
  onWrong,
  onNext,
  onEnd,
  onConfetti,
}) {
  const [status, setStatus] = useState('ready');
  const [result, setResult] = useState({ text: '', type: '' });
  const [failedAttempts, setFailedAttempts] = useState(0);

  const recognizingRef = useRef(false);
  const answeredRef = useRef(false);

  function reset() {
    answeredRef.current = false;
    recognizingRef.current = false;
    setStatus('ready');
    setResult({ text: '', type: '' });
    setFailedAttempts(0); // Reinicia los intentos con cada palabra nueva
  }

  useEffect(() => {
    reset();
  }, [currentIndex]);

  function toggle(word) {
    if (recognizingRef.current || answeredRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 5;

    recognizingRef.current = true;
    setStatus('listening');
    setResult({ text: 'Listening...', type: 'neutral' });

    rec.onresult = (e) => {
      const transcripts = [];
      for (let i = 0; i < e.results.length; i++)
        for (let j = 0; j < e.results[i].length; j++)
          if (e.results[i][j].transcript)
            transcripts.push(e.results[i][j].transcript);

      const q = questionsRef.current[currentIdxRef.current];
      const wordAlts = q?.word?.alts || [];

      recognizingRef.current = false;
      if (!mountedRef.current) return;

      let matchResult = { match: false, fullSim: 0, bestWordSim: 0 };
      let bestHeard = transcripts[0] || '';

      for (const transcript of transcripts) {
        const attempt = smartMatch(transcript, word, wordAlts);
        if (attempt.match) {
          matchResult = attempt;
          bestHeard = transcript;
          break;
        }
        const partial = Math.max(attempt.fullSim || 0, attempt.bestWordSim || 0);
        const current = Math.max(matchResult.fullSim || 0, matchResult.bestWordSim || 0);
        if (partial > current) { matchResult = attempt; bestHeard = transcript; }
      }

      if (matchResult.match) {
        // 🎯 ¡ACIERTO PERFECTO!
        setStatus('done');
        answeredRef.current = true;
        setResult({ text: `✓ "${bestHeard}" — ¡Correcto!`, type: 'heard' });
        onCorrect(q.mode);
        onConfetti();
        setTimeout(() => { if (mountedRef.current) onNext(); }, 1200);

      } else {
        // ❌ FALLÓ LA PRONUNCIACIÓN
        const newFails = failedAttempts + 1;
        setFailedAttempts(newFails);

        if (newFails >= 3) {
          // 💀 TERCER STRIKE: PIERDE VIDA Y SE ACABA EL TURNO
          setStatus('done');
          answeredRef.current = true;
          setResult({ text: `✗ Escuché: "${bestHeard}". ¡Se acabaron los intentos!`, type: 'miss' });
          
          const newLives = onWrong(); // 👈 SOLO AQUÍ PIERDE LA VIDA
          
          setTimeout(() => {
            if (!mountedRef.current) return;
            if (newLives <= 0) onEnd();
            else onNext();
          }, 2000);

        } else {
          // ⚠️ STRIKE 1 O 2: SIGUE INTENTANDO
          setStatus('error');
          setResult({ text: `✗ Escuché: "${bestHeard}" (Intento ${newFails}/3)`, type: 'miss' });
          
          setTimeout(() => {
            if (!mountedRef.current) return;
            setStatus('ready'); // Enciende el botón para el siguiente intento
          }, 2000); // 2 segundos para que lea en qué se equivocó
        }
      }
    };

    rec.onerror = (err) => {
      recognizingRef.current = false;
      if (!mountedRef.current) return;

      setStatus('error');
      const msg =
        err.error === 'no-speech'   ? 'No te escuché — intenta de nuevo'      :
        err.error === 'not-allowed' ? 'Micrófono bloqueado — revisa permisos' :
        err.error === 'network'     ? 'Error de red — intenta de nuevo'       :
                                      'No pude entender — intenta de nuevo';

      setResult({ text: msg, type: 'miss' });

      // Los errores técnicos NO quitan vidas, solo resetean el botón
      setTimeout(() => {
        if (!mountedRef.current) return;
        setStatus('ready');
      }, 1500);
    };

    rec.onend = () => { 
      recognizingRef.current = false; 
      setStatus(prev => prev === 'listening' ? 'ready' : prev); 
    };

    rec.start();
  }

  return {
    status,          
    result,          
    toggle,          
    reset,           
    isListening: status === 'listening',
    isSupported: !!(window.SpeechRecognition || window.webkitSpeechRecognition),
    failedAttempts
  };
}